/**
 * Teammates' synced Google/Outlook calendar events for a window, read from
 * the server's per-user cache (`GET /api/calendar/events`).
 */
import axiosInstance from "./axiosConfig";
import { handleAPIError } from "./utils/errors";

export type CalendarSourceStatus = "ok" | "unavailable" | "none";

export type CalendarSource = {
  uid: string;
  name: string;
  provider: "google" | "outlook";
  status: CalendarSourceStatus;
  synced_at: string | null;
};

export type SyncedCalendarEvent = {
  id: string;
  uid: string;
  start: string;
  end: string | null;
  all_day: boolean;
  title: string;
  source: "google" | "outlook";
  html_link?: string | null;
};

export type CalendarEventsResponse = {
  events: SyncedCalendarEvent[];
  sources: CalendarSource[];
  synced_at: string | null;
};

export type CalendarEventsQuery = {
  from: string;
  to: string;
  /** Bypass the server cache and pull from the providers again. */
  refresh?: boolean;
};

export const CalendarEventsAPI = {
  async list({
    from,
    to,
    refresh
  }: CalendarEventsQuery): Promise<CalendarEventsResponse> {
    try {
      const { data } = await axiosInstance.get<CalendarEventsResponse>(
        "/calendar/events",
        { params: { from, to, ...(refresh ? { refresh: 1 } : {}) } }
      );
      return {
        events: Array.isArray(data?.events) ? data.events : [],
        sources: Array.isArray(data?.sources) ? data.sources : [],
        synced_at: data?.synced_at ?? null
      };
    } catch (error) {
      const apiError = handleAPIError(error);
      throw apiError;
    }
  }
};
